import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Autocomplete
} from '@mui/material';
import { useAuth } from '../auth/AuthProvider';
import { createInvitation } from '../../api/invitations';
import adminApi from '../../services/adminApi';
import { UserInvitationRequest, UserRole, Country } from '../../types';

interface CreateUserInvitationProps {
  onInvitationCreated: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const CreateUserInvitation: React.FC<CreateUserInvitationProps> = ({ onInvitationCreated }) => {
  const { tokens } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState<UserInvitationRequest>({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    role: 'user',
    value_chain_permissions: [],
    country_permissions: [],
    data_permissions: [],
    expires_in_hours: 168 // 7 days
  });

  // Dropdown data
  const [countries, setCountries] = useState<Country[]>([]);
  const [graphSources, setGraphSources] = useState<Array<{ source_db: string; label: string }>>([]);
  const [selectedGraphSources, setSelectedGraphSources] = useState<string[]>([]);
  // Financial sources come as { country, source, label }
  const [financialSources, setFinancialSources] = useState<Array<{ country: string; source: string; label: string }>>([]);
  const [selectedFinancialCombos, setSelectedFinancialCombos] = useState<Array<{ country: string; source: string }>>([]);
  const [dataTypes] = useState<string[]>([
    'admin',
    'prodcom_view'
  ]);

  // Load dropdown data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [countriesData, sourcesData, financialResp] = await Promise.all([
          adminApi.getCountries(),
          adminApi.getGraphSources(true),
          adminApi.getFinancialSources()
        ]);
        setCountries(countriesData);
        setGraphSources(Array.isArray(sourcesData) ? sourcesData : []);
        const finArr = Array.isArray((financialResp as any)?.data) ? (financialResp as any).data : [];
        setFinancialSources(finArr);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
        setError('Failed to load dropdown data');
      }
    };

    loadData();
  }, []);

  const handleInputChange = (field: keyof UserInvitationRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field: 'value_chain_permissions' | 'country_permissions' | 'data_permissions', value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!tokens?.accessToken) {
        throw new Error('No access token available');
      }

      // Encode selected graph sources into data_permissions as 'graph_source:<source>'
      const encodedGraphSources = (selectedGraphSources || []).map((s) => `graph_source:${s}`);
      // Encode selected financial combos into data_permissions as 'financial:<country>:<source>'
      const encodedFinancial = (selectedFinancialCombos || []).map(({ country, source }) => `financial:${country}:${source}`);
      const payload: UserInvitationRequest = {
        ...formData,
        data_permissions: [...(formData.data_permissions || []), ...encodedGraphSources, ...encodedFinancial]
      };
      const response = await createInvitation(payload, tokens.accessToken);
      setSuccess(`Invitation created successfully! Invitation ID: ${response.invitation_id}`);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        company: '',
        role: 'user',
        value_chain_permissions: [],
        country_permissions: [],
        data_permissions: [],
        expires_in_hours: 168
      });
      setSelectedGraphSources([]);
      setSelectedFinancialCombos([]);
      onInvitationCreated();
    } catch (err: any) {
      console.error('Error creating invitation:', err);
      setError(err.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.email && formData.email.includes('@');
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Create New User Invitation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Invite a new user to the system with predefined permissions and roles.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                  disabled={loading}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="superuser">Super User</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                disabled={loading}
              />
            </Grid>

            {/* Permissions */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Permissions
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={countries}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={countries.filter(c => (formData.country_permissions || []).includes(c.code))}
                onChange={(event, newValue) => {
                  // Store country NAMES in the invitation so backend can insert name=name
                  handleMultiSelectChange('country_permissions', newValue.map(c => c.name));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country Access"
                    placeholder="Search countries..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.code}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                disabled={loading}
                filterSelectedOptions
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Data Access</InputLabel>
                <Select
                  multiple
                  value={formData.data_permissions || []}
                  onChange={(e) => handleMultiSelectChange('data_permissions', e.target.value as string[])}
                  input={<OutlinedInput label="Data Access" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value.replace('_', ' ')} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  disabled={loading}
                >
                  {dataTypes.map((type) => {
                    const getDisplayName = (type: string) => {
                      switch (type) {
                        case 'admin': return 'Admin Access';
                        case 'prodcom_view': return 'PRODCOM View';
                        default: return type.replace('_', ' ').toUpperCase();
                      }
                    };
                    
                    return (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={(formData.data_permissions || []).indexOf(type) > -1} />
                        <ListItemText primary={getDisplayName(type)} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Graph Source Access (encoded into data_permissions) */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={graphSources}
                  getOptionLabel={(option) => option.label}
                  value={graphSources.filter(gs => selectedGraphSources.includes(gs.source_db))}
                  onChange={(_, newValue) => {
                    setSelectedGraphSources(newValue.map(v => v.source_db));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Graph Source Access"
                      placeholder="Search graph sources..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.source_db}
                        label={option.source_db}
                        size="small"
                      />
                    ))
                  }
                  disabled={loading}
                  filterSelectedOptions
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Selected sources are granted as data permissions with type 'graph_source'.
                </Typography>
              </FormControl>
            </Grid>

            {/* Financial Data Access (encoded into data_permissions) */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={financialSources}
                  getOptionLabel={(option) => option.label}
                  value={selectedFinancialCombos.map(sel => {
                    const found = financialSources.find(fs => fs.country === sel.country && fs.source === sel.source);
                    return found || { country: sel.country, source: sel.source, label: `${sel.country} • ${sel.source}` };
                  })}
                  onChange={(_, newValue) => {
                    setSelectedFinancialCombos(newValue.map((v: any) => ({ country: v.country, source: v.source })));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Financial Data Access"
                      placeholder="Select (country • source) combinations..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={`${option.country}-${option.source}`}
                        label={`${option.country} • ${option.source}`}
                        size="small"
                      />
                    ))
                  }
                  disabled={loading}
                  filterSelectedOptions
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Selected items are granted as financial permissions (country + source).
                </Typography>
              </FormControl>
            </Grid>

            {/* Expiration */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiration (hours)"
                type="number"
                value={formData.expires_in_hours}
                onChange={(e) => handleInputChange('expires_in_hours', parseInt(e.target.value) || 168)}
                disabled={loading}
                helperText="Invitation will expire after this many hours (default: 168 = 7 days)"
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid() || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Creating Invitation...' : 'Create Invitation'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      email: '',
                      first_name: '',
                      last_name: '',
                      company: '',
                      role: 'user',
                      value_chain_permissions: [],
                      country_permissions: [],
                      data_permissions: [],
                      expires_in_hours: 168
                    });
                    setError(null);
                    setSuccess(null);
                  }}
                  disabled={loading}
                >
                  Reset Form
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};